// @ts-nocheck
import { useState, useEffect } from "react";
import { Package, Plus, Pencil, Eye, Trash2, Search, Filter, ArrowUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { supabase } from '@/integrations/supabase/client';

const CATEGORIAS = [
  'Alimentos e Bebidas',
  'Produtos de Limpeza',
  'Utensílios de Copa e Cozinha',
  'Eletrodomésticos e Eletrônicos',
  'Material de Escritório',
  'Móveis e Decoração',
  'Livros',
  'Equipamentos de Proteção',
  'Ferramentas e Acessórios'
];

type Produto = {
  id: string;
  codigo_produto: string;
  nome_produto: string;
  categoria: string;
  valor: number;
  quantidade_minima: number;
  ativo: boolean;
  created_at: string;
  updated_at: string;
  created_by?: string;
  updated_by?: string;
  saldo_atual: number;
};

type ProdutoFormData = {
  nome_produto: string;
  categoria: string;
  valor: number;
  quantidade_minima: number;
};

export default function ProdutosInsumos() {
  const { toast } = useToast();
  const { user } = useAuth();
  const { hasPermission, loading: permissionsLoading } = usePermissions();
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState<keyof Produto | "">("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [statusFilter, setStatusFilter] = useState<"all" | "Ativo" | "Inativo">("all");
  const [loading, setLoading] = useState(true);
  
  // Estados dos modais
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedProduto, setSelectedProduto] = useState<Produto | null>(null);
  const [produtoToDelete, setProdutoToDelete] = useState<Produto | null>(null);
  
  // Formulário
  const [formData, setFormData] = useState<ProdutoFormData>({
    nome_produto: "",
    categoria: "",
    valor: 0,
    quantidade_minima: 0,
  });

  const [produtos, setProdutos] = useState<Produto[]>([]);

  // Permission checks - only after permissions are loaded
  const canReadProdutos = !permissionsLoading && hasPermission('produtos_insumos', 'produtos_insumos', 'read');
  const canWriteProdutos = !permissionsLoading && hasPermission('produtos_insumos', 'produtos_insumos', 'write');
  const canAdminProdutos = !permissionsLoading && hasPermission('produtos_insumos', 'produtos_insumos', 'admin');

  useEffect(() => {
    if (!permissionsLoading) {
      if (canReadProdutos) {
        fetchProdutos();
      } else {
        setLoading(false);
      }
    }
  }, [permissionsLoading, canReadProdutos]);

const fetchProdutos = async () => {
  try {
    const { data: produtosData, error: produtosError } = await supabase
      .from('produtos_insumos')
      .select('*')
      .eq('ativo', true)
      .order('created_at', { ascending: false });

    if (produtosError) throw produtosError;

    // Buscar as movimentações para calcular o saldo atual de cada produto
    const { data: movs, error: movsError } = await supabase
      .from('produtos_entrada_saida')
      .select('produto_id, saldo_atual, data_movimentacao');

    if (movsError) throw movsError;

    // Obter o último saldo_atual por produto (movimentação mais recente)
    const latestSaldo = new Map<string, { saldo: number; data: string }>();
    (movs || []).forEach((m: any) => {
      const curr = latestSaldo.get(m.produto_id);
      if (!curr || new Date(m.data_movimentacao) > new Date(curr.data)) {
        latestSaldo.set(m.produto_id, { saldo: Number(m.saldo_atual) || 0, data: m.data_movimentacao });
      }
    });

    const withSaldo = (produtosData || []).map((p: any) => ({
      ...p,
      saldo_atual: latestSaldo.get(p.id)?.saldo ?? 0,
    }));

    setProdutos(withSaldo);
  } catch (error) {
    console.error('Erro ao buscar produtos:', error);
    toast({
      title: "Erro",
      description: "Erro ao carregar produtos",
      variant: "destructive",
    });
  } finally {
    setLoading(false);
  }
};

  const resetForm = () => {
    setFormData({
      nome_produto: "",
      categoria: "",
      valor: 0,
      quantidade_minima: 0,
    });
  };

  const handleSort = (field: keyof Produto) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const handleCreate = () => {
    resetForm();
    setIsCreateDialogOpen(true);
  };

  const handleEdit = (produto: Produto) => {
    setSelectedProduto(produto);
    setFormData({
      nome_produto: produto.nome_produto,
      categoria: produto.categoria,
      valor: produto.valor,
      quantidade_minima: produto.quantidade_minima,
    });
    setIsEditDialogOpen(true);
  };

  const handleView = (produto: Produto) => {
    setSelectedProduto(produto);
    setIsViewDialogOpen(true);
  };

  const handleDeleteClick = (produto: Produto) => {
    setProdutoToDelete(produto);
    setIsDeleteDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.nome_produto || !formData.categoria) {
      toast({
        title: "Erro",
        description: "Preencha todos os campos obrigatórios",
        variant: "destructive",
      });
      return;
    }

    try {
      if (selectedProduto) {
        // Editar
        const { error } = await supabase
          .from('produtos_insumos')
          .update({
            ...formData,
            updated_by: user?.id,
          })
          .eq('id', selectedProduto.id);

        if (error) throw error;
        
        toast({
          title: "Sucesso",
          description: "Produto atualizado com sucesso",
        });
        setIsEditDialogOpen(false);
      } else {
        // Criar
        const { error } = await supabase
          .from('produtos_insumos')
          .insert({
            ...formData,
            created_by: user?.id,
          });

        if (error) throw error;
        
        toast({
          title: "Sucesso",
          description: "Produto criado com sucesso",
        });
        setIsCreateDialogOpen(false);
      }
      
      resetForm();
      setSelectedProduto(null);
      fetchProdutos();
    } catch (error) {
      console.error('Erro ao salvar produto:', error);
      toast({
        title: "Erro",
        description: "Erro ao salvar produto",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async () => {
    if (!produtoToDelete) return;

    try {
      const { error } = await supabase
        .from('produtos_insumos')
        .update({ 
          ativo: false,
          updated_by: user?.id,
        })
        .eq('id', produtoToDelete.id);

      if (error) throw error;
      
      toast({
        title: "Sucesso",
        description: "Produto excluído com sucesso",
      });
      setIsDeleteDialogOpen(false);
      setProdutoToDelete(null);
      fetchProdutos();
    } catch (error) {
      console.error('Erro ao excluir produto:', error);
      toast({
        title: "Erro",
        description: "Erro ao excluir produto",
        variant: "destructive",
      });
    }
  };

  const handleStatusFilter = (status: "all" | "Ativo" | "Inativo") => {
    setStatusFilter(status);
  };

  // Filtrar e ordenar produtos
  let filteredProdutos = produtos.filter(produto => {
    const matchesSearch = produto.nome_produto.toLowerCase().includes(searchTerm.toLowerCase()) ||
      produto.codigo_produto.toLowerCase().includes(searchTerm.toLowerCase()) ||
      produto.categoria.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === "all" || 
      (statusFilter === "Ativo" && produto.ativo) ||
      (statusFilter === "Inativo" && !produto.ativo);
    
    return matchesSearch && matchesStatus;
  });

  if (sortField) {
    filteredProdutos = filteredProdutos.sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];
      
      if (typeof aValue === "string" && typeof bValue === "string") {
        return sortDirection === "asc" 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortDirection === "asc" ? aValue - bValue : bValue - aValue;
      }
      
      return 0;
    });
  }

  if (!canReadProdutos) {
    return (
      <ProtectedRoute>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-2">Acesso Negado</h2>
            <p className="text-muted-foreground">Você não tem permissão para acessar esta página.</p>
          </div>
        </div>
      </ProtectedRoute>
    );
  }

  if (loading) {
    return (
      <ProtectedRoute>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
        </div>
      </ProtectedRoute>
    );
  }

  return (
    <ProtectedRoute>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Produtos Insumos</h1>
            <p className="text-muted-foreground">
              Visualize e gerencie todos os produtos insumos cadastrados
            </p>
          </div>
          {canWriteProdutos && (
            <Button onClick={handleCreate}>
              <Plus className="mr-2 h-4 w-4" />
              Novo Produto
            </Button>
          )}
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Lista de Produtos Insumos
            </CardTitle>
            <CardDescription>
              Visualize e gerencie todos os produtos insumos cadastrados
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center space-x-2 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por nome, código ou categoria..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8"
                />
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline">
                    <Filter className="mr-2 h-4 w-4" />
                    Filtros
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem onClick={() => handleStatusFilter("all")}>
                    Todos os status
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleStatusFilter("Ativo")}>
                    Apenas ativos
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleStatusFilter("Inativo")}>
                    Apenas inativos
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {filteredProdutos.length === 0 ? (
              <div className="text-center py-8">
                <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">Nenhum produto encontrado</h3>
                <p className="text-muted-foreground">
                  {searchTerm ? "Tente ajustar os filtros de busca." : "Ainda não há produtos cadastrados."}
                </p>
              </div>
            ) : (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
<TableHead 
  className="cursor-pointer hover:bg-muted/50"
  onClick={() => handleSort("codigo_produto")}
>
  <div className="flex items-center space-x-2">
    <span>Código</span>
    <ArrowUpDown className="h-4 w-4" />
  </div>
</TableHead>
<TableHead 
  className="cursor-pointer hover:bg-muted/50"
  onClick={() => handleSort("nome_produto")}
>
  <div className="flex items-center space-x-2">
    <span>Nome</span>
    <ArrowUpDown className="h-4 w-4" />
  </div>
</TableHead>
<TableHead 
  className="cursor-pointer hover:bg-muted/50"
  onClick={() => handleSort("categoria")}
>
  <div className="flex items-center space-x-2">
    <span>Categoria</span>
    <ArrowUpDown className="h-4 w-4" />
  </div>
</TableHead>
<TableHead 
  className="cursor-pointer hover:bg-muted/50"
  onClick={() => handleSort("valor")}
>
  <div className="flex items-center space-x-2">
    <span>Preço</span>
    <ArrowUpDown className="h-4 w-4" />
  </div>
</TableHead>
<TableHead 
  className="cursor-pointer hover:bg-muted/50"
  onClick={() => handleSort("quantidade_minima")}
>
  <div className="flex items-center space-x-2">
    <span>Qtd. Mínima</span>
    <ArrowUpDown className="h-4 w-4" />
  </div>
</TableHead>
<TableHead 
  className="cursor-pointer hover:bg-muted/50"
  onClick={() => handleSort("saldo_atual")}
>
  <div className="flex items-center space-x-2">
    <span>Saldo Atual</span>
    <ArrowUpDown className="h-4 w-4" />
  </div>
</TableHead>
<TableHead>Status</TableHead>
<TableHead>Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredProdutos.map((produto) => (
                      <TableRow key={produto.id}>
<TableCell className="font-medium">{produto.codigo_produto}</TableCell>
<TableCell>{produto.nome_produto}</TableCell>
<TableCell>{produto.categoria}</TableCell>
<TableCell>R$ {produto.valor.toFixed(2)}</TableCell>
<TableCell>{produto.quantidade_minima}</TableCell>
<TableCell>{produto.saldo_atual ?? 0}</TableCell>
<TableCell>
  <Badge 
    variant={produto.ativo ? "default" : "secondary"}
  >
    {produto.ativo ? "Ativo" : "Inativo"}
  </Badge>
</TableCell>
<TableCell>
  <div className="flex items-center space-x-2">
    <Button
      variant="outline"
      size="sm"
      title="Visualizar"
      onClick={() => handleView(produto)}
    >
      <Eye className="h-4 w-4" />
    </Button>
    {canWriteProdutos && (
      <Button
        variant="outline"
        size="sm"
        title="Editar"
        onClick={() => handleEdit(produto)}
      >
        <Pencil className="h-4 w-4" />
      </Button>
    )}
    {canAdminProdutos && (
      <Button
        variant="outline"
        size="sm"
        title="Excluir"
        onClick={() => handleDeleteClick(produto)}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    )}
  </div>
</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Modal Criar/Editar Produto */}
        <Dialog open={isCreateDialogOpen || isEditDialogOpen} onOpenChange={() => {
          setIsCreateDialogOpen(false);
          setIsEditDialogOpen(false);
          resetForm();
          setSelectedProduto(null);
        }}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {selectedProduto ? "Editar Produto" : "Novo Produto"}
              </DialogTitle>
              <DialogDescription>
                {selectedProduto ? "Edite as informações do produto" : "Preencha as informações do novo produto"}
              </DialogDescription>
            </DialogHeader>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="nome_produto">Nome do Produto *</Label>
                <Input
                  id="nome_produto"
                  value={formData.nome_produto}
                  onChange={(e) => setFormData(prev => ({ ...prev, nome_produto: e.target.value }))}
                  placeholder="Nome do produto"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="categoria">Categoria *</Label>
                <Select value={formData.categoria} onValueChange={(value) => setFormData(prev => ({ ...prev, categoria: value }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione uma categoria" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIAS.map((categoria) => (
                      <SelectItem key={categoria} value={categoria}>
                        {categoria}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="valor">Valor</Label>
                <Input
                  id="valor"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.valor}
                  onChange={(e) => setFormData(prev => ({ ...prev, valor: parseFloat(e.target.value) || 0 }))}
                  placeholder="0.00"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="quantidade_minima">Quantidade Mínima</Label>
                <Input
                  id="quantidade_minima"
                  type="number"
                  min="0"
                  value={formData.quantidade_minima}
                  onChange={(e) => setFormData(prev => ({ ...prev, quantidade_minima: parseInt(e.target.value) || 0 }))}
                  placeholder="0"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setIsCreateDialogOpen(false);
                setIsEditDialogOpen(false);
                resetForm();
                setSelectedProduto(null);
              }}>
                Cancelar
              </Button>
              <Button onClick={handleSave}>
                {selectedProduto ? "Salvar Alterações" : "Criar Produto"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal Visualizar Produto */}
        <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Detalhes do Produto</DialogTitle>
              <DialogDescription>
                Informações completas do produto
              </DialogDescription>
            </DialogHeader>
            
            {selectedProduto && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Código</Label>
                  <p className="text-sm">{selectedProduto.codigo_produto}</p>
                </div>
                
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Nome</Label>
                  <p className="text-sm">{selectedProduto.nome_produto}</p>
                </div>
                
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Categoria</Label>
                  <p className="text-sm">{selectedProduto.categoria}</p>
                </div>
                
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Valor</Label>
                  <p className="text-sm">R$ {selectedProduto.valor.toFixed(2)}</p>
                </div>
                
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Quantidade Mínima</Label>
                  <p className="text-sm">{selectedProduto.quantidade_minima}</p>
                </div>
                
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Status</Label>
                  <div className="pt-1">
                    <Badge variant={selectedProduto.ativo ? "default" : "secondary"}>
                      {selectedProduto.ativo ? "Ativo" : "Inativo"}
                    </Badge>
                  </div>
                </div>
                
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Criado em</Label>
                  <p className="text-sm">{new Date(selectedProduto.created_at).toLocaleDateString('pt-BR')}</p>
                </div>
                
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Atualizado em</Label>
                  <p className="text-sm">{new Date(selectedProduto.updated_at).toLocaleDateString('pt-BR')}</p>
                </div>
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsViewDialogOpen(false)}>
                Fechar
              </Button>
              {selectedProduto && canWriteProdutos && (
                <Button onClick={() => {
                  setIsViewDialogOpen(false);
                  handleEdit(selectedProduto);
                }}>
                  Editar
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal Confirmar Exclusão */}
        <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
              <AlertDialogDescription>
                Tem certeza que deseja excluir o produto "{produtoToDelete?.nome_produto}"? 
                Esta ação não pode ser desfeita.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setProdutoToDelete(null)}>
                Cancelar
              </AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                Excluir
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </ProtectedRoute>
  );
}